import React from 'react'
import * as Sentry from '@sentry/react'
import { theme } from '@notino/react-styleguide'

// styles
import * as SC from './ReservationStatusBadgeStyles'

// utils
import { RESERVATION_STATE } from '../../utils/enums'
import { getIntl } from '../../utils/intl'

type Props = {
	reservationState: RESERVATION_STATE
	size?: 'small' | 'default'
	className?: string
}

const getBadgeData = (
	reservationState: RESERVATION_STATE
): {
	// NOTE: not all colors that are in the design exists in the theme
	backgroundColor: string
	labelColor: string
	label: string
} | null => {
	const intl = getIntl()

	switch (reservationState) {
		case RESERVATION_STATE.APPROVED:
			return {
				backgroundColor: '#D1FAE5',
				labelColor: theme.color.text.positive,
				label: intl.formatMessage({ id: 'Confirmed', defaultMessage: 'Confirmed' })
			}
		case RESERVATION_STATE.PENDING:
			return {
				backgroundColor: '#FEF3C7',
				labelColor: '#B45309',
				label: intl.formatMessage({ id: 'Waiting for confirmation', defaultMessage: 'Waiting for confirmation' })
			}
		case RESERVATION_STATE.DECLINED:
		case RESERVATION_STATE.CANCEL_BY_CUSTOMER:
		case RESERVATION_STATE.CANCEL_BY_SALON:
		case RESERVATION_STATE.CANCEL_BY_SYSTEM:
			return {
				backgroundColor: '#FFE4E6',
				labelColor: theme.color.text.negative,
				label: intl.formatMessage({ id: 'Cancelled', defaultMessage: 'Cancelled' })
			}
		case RESERVATION_STATE.REALIZED:
		case RESERVATION_STATE.NOT_REALIZED:
			return {
				backgroundColor: theme.color.background.secondary,
				labelColor: theme.color.text.tertiary,
				label: intl.formatMessage({ id: 'Completed reservation', defaultMessage: 'Completed reservation' })
			}
		default: {
			Sentry.captureMessage(`Unsupported reservation state: "${reservationState}". Null is returned from the component`)
			return null
		}
	}
}

const ReservationStatusBadge = (props: Props) => {
	const { reservationState, size = 'default', className = '' } = props

	const badgeData = getBadgeData(reservationState)

	if (!badgeData) {
		return null
	}

	const { backgroundColor, labelColor, label } = badgeData

	return (
		<SC.Badge $backgroundColor={backgroundColor} $textColor={labelColor} $size={size} className={className}>
			{label}
		</SC.Badge>
	)
}

export default ReservationStatusBadge
